import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../../TokenHelper';
import { ConvertionHelpers } from '../../ConvertionHelpers';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { Article, dataActionCreators, DataState, Invoice, ProjectAssigment, Sale } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { useEditorStyles, useStackClassName } from '../Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, InputOnChangeData, Link, Textarea, TextareaOnChangeData, Toast, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { onFormatDate } from '../Tools/converters';
import { InvoiceDropdown } from '../EditorHelpers/InvoiceDropdown';
import { CustomerDropdown } from '../EditorHelpers/CustomerDropdown';
import { ArticleDropdown } from '../EditorHelpers/ArticleDropdown';
import { CurrencyInput } from '../EditorHelpers/CurrencyInput';
import { FileViewer } from '../FileViewer/FileViewer';
import { DownloadFile } from '../Tools/FileDownloadHelper';
import { useCustomers, useInvoices } from '../Tools/ObjectLoader';
import { VATDropdown } from '../EditorHelpers/VATDropdown';

export const GetProjectAssignments = (data: DataState, customerid: string | null, projectid: string | null, consultantid: string | null): ProjectAssigment[] => {
    let pa: ProjectAssigment[] = data.projectsAssigments ? data.projectsAssigments : [];
    pa = pa.filter(x => x.dateFin === null || (new Date(x.dateFin).getTime() >= Date.now()));
    if (projectid) {
        pa = pa.filter(x => x.projectid === projectid);
    }
    if (consultantid) {
        pa = pa.filter(x => x.consultantid === consultantid);
    }
    if (customerid && data.projects) {
        const ps = data.projects.filter(x => x.customerid === customerid);
        pa = pa.filter(x => ps.findIndex(y => y.projectid === x.projectid) !== -1);
    }
    return pa;
};

export const SalesEditor = () => {
    // All data needed for edition should be loaded before calling the editor
    const data = useSelector<ApplicationState, DataState>((state: ApplicationState) => state.data);
    const { customers } = useCustomers();
    const { invoices } = useInvoices();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const [article, setArticle] = React.useState<Article | null | undefined>(data.salesEdit && data.salesEdit.articleid && data.articles ? data.articles.find(x => data.salesEdit && x.articleid === data.salesEdit.articleid) : undefined);
    const [date, setDate] = React.useState<Date | null>(data.salesEdit && data.salesEdit.date ? new Date(data.salesEdit.date) : null);
    const [customer, setCustomer] = React.useState<Customer | null | undefined>(customers ? customers.find((c) => data.salesEdit && c.customerid === data.salesEdit.customerid) : undefined);
    const [description, setDescription] = React.useState<string | null>(data.salesEdit && data.salesEdit.description);
    const [count, setCount] = React.useState<number>(data.salesEdit && data.salesEdit.count ? data.salesEdit.count : 0);
    const [unitPrice, setUnitPrice] = React.useState<number>(data.salesEdit && data.salesEdit.unitPrice ? data.salesEdit.unitPrice : 0);
    const [vatcode, setVatcode] = React.useState<number>(data.salesEdit ? data.salesEdit.vatcode : 21);
    const [invoiceid, setInvoiceid] = React.useState<string | null>(data.salesEdit && data.salesEdit.invoiceid);
    const [orderRef, setOrderRef] = React.useState<string | null>(data.salesEdit && data.salesEdit.orderRef);
    const [buyerItemIdentification, setBuyerItemIdentification] = React.useState<string | null>(data.salesEdit && data.salesEdit.buyerItemIdentification);
    const [sellerItemIdentification, setSellerItemIdentification] = React.useState<string | null>(data.salesEdit && data.salesEdit.sellerItemIdentification);
    // Fields validation
    const [isDateValid, setDateValid] = React.useState(false);
    const [isCountValid, setCountValid] = React.useState(false);
    const [isProjectAssignmentValid, setProjectAssignmentValid] = React.useState(false);
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);
    // Dependencies
    const [invoicesFilter, setInvoicesFilter] = React.useState<Invoice[]>([]);
    const isEditAndFile = React.useMemo(() => { return (data.salesEdit && data.salesEdit.orderSharePointFileUniqueId) ?? false; }, [data.salesEdit]);
    const [file, setFile] = React.useState<Blob>();
    React.useEffect(() => {
        if (isEditAndFile)
            TokenHelper.GetToken(instance, teamsState)
                .then(token => {
                    if (token !== null && data.salesEdit && data.salesEdit.sellid) {
                        DownloadFile('api/Sales/' + data.salesEdit.sellid + "/DownloadSellOrderFile", token)
                            .then((f) => {
                                if (f) {
                                    setFile(f);
                                }
                            })
                            .catch((e) => { console.log(e); });
                    }
                });
    }, [isEditAndFile]);

    React.useEffect(() => {
        if (invoices !== undefined && customer !== undefined && customer !== null) {
            setInvoicesFilter(invoices.filter(i => i.customerid === customer.customerid && (i.paymentDate === null || i.paymentDate.length === 0) && i.invoiceDate && (new Date(i.invoiceDate)).getFullYear() > 2022));
        }
    }, [invoices, customer]);

    React.useEffect(() => {
        setProjectAssignmentValid(article !== null && article !== undefined);
    }, [article]);
    React.useEffect(() => {
        setDateValid(date !== null);
    }, [date]);
    React.useEffect(() => {
        setCountValid(count > 0);
    }, [count]);
    React.useEffect(() => {
        const submitValid = isProjectAssignmentValid && isDateValid && isCountValid;
        //if (submitValid && data.salesEdit) {
        //    // If valid, check if any change on existing item
        //    //submitValid = submitValid ;
        //}
        setSubmitValid(submitValid);
    }, [
        isProjectAssignmentValid, isDateValid, isCountValid
    ]);

    const submit = () => {
        const c: Sale = {
            sellid: data.salesEdit ? data.salesEdit.sellid : null,
            date: ConvertionHelpers.DateToISODateOnly(date),
            articleid: article ? article.articleid : null,
            customerid: customer ? customer.customerid : null,
            description: description,
            count: count,
            unitPrice: unitPrice ? unitPrice : (article ? article.unitPrice : 0),
            vatcode: vatcode,
            invoiceid: invoiceid,
            orderRef: orderRef,
            orderSharePointFileUniqueId: null,
            buyerItemIdentification: buyerItemIdentification,
            sellerItemIdentification: sellerItemIdentification,
        };

        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    const toastId = (Math.random() + 1).toString(36).substring(7);
                    dispatchToast(
                        <Toast appearance="inverted">
                            <ToastTitle>
                                Saving Sale...
                            </ToastTitle>
                        </Toast>,
                        { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                    );
                    fetch("api/Sales/" + (data.salesEdit && data.salesEdit.sellid !== null ? data.salesEdit.sellid : ""), {
                        method: data.salesEdit && data.salesEdit.sellid === null ? "POST" : "PUT",  // if customerid exist, path
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        body: JSON.stringify(c),
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 200) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                Sale saved
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                });
                                return response.json() as Promise<Sale>;
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                Sale save failed
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                });
                            }
                        })
                        .then(data => {
                            if (data) {
                                dispatch(dataActionCreators.patchSaleEdition(data));
                                dispatch(dataActionCreators.requestSaleEdition(null));
                            }
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface className={isEditAndFile ? styles.DialogContentWithFilePreview : undefined}>
            <DialogBody className={isEditAndFile ? styles.fluid : undefined}>
                <DialogTitle>{data.salesEdit && data.salesEdit.sellid !== null ? "Edit sale" : "Create new sale"}</DialogTitle>
                <DialogContent>
                    <div className={styles.onSingleLinWithFilePreviewe}>
                        {isEditAndFile &&
                            <FileViewer file={file} />
                        }
                        <div className={isEditAndFile ? styles.InfoPart : styles.fluid}>
                            <form
                                onSubmit={() => {
                                    dispatch(dataActionCreators.requestSaleEdition(null));
                                }}
                                className={useStackClassName()}
                                autoComplete="off"
                            >
                                <Field label={t('Customer')} required>
                                    <CustomerDropdown currentCustomer={customer ?? undefined}
                                        customers={customers ?? []}
                                        setCustomer={setCustomer} />
                                </Field>
                                <Field label="Article" className={styles.fluid} required>
                                    {/*error={!isProjectAssignmentValid}*/}
                                    <ArticleDropdown
                                        currentArticle={article ?? undefined}
                                        setArticle={(a) => {
                                            setArticle(a);
                                            setUnitPrice(a ? a.unitPrice : 0);
                                        }} />
                                </Field>
                                <Field label="Date" className={styles.fluid}
                                    required
                                    validationMessage={isDateValid ? undefined : "Date not valid"}
                                >
                                    <DatePicker className={styles.fluid}
                                        onSelectDate={(date: Date | null | undefined) => { setDate(date === undefined ? null : date); }}
                                        value={date === null ? undefined : date}
                                        formatDate={onFormatDate}
                                        placeholder={t('SelectSaleDate')}
                                    />
                                </Field>
                                <Field label="Order Ref">
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setOrderRef(data.value)}
                                        value={orderRef !== null ? orderRef : ""}
                                    />
                                </Field>
                                <Field label="Description" className={styles.fluid}>
                                    <Textarea resize="vertical"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => { setDescription(data.value); }}
                                        value={description ? description : undefined}
                                    />
                                </Field>
                                <Field label={t('Quantity')} required className={styles.fluid}
                                    validationMessage={isCountValid ? undefined : "Count not valid"}>
                                    <CurrencyInput
                                        setCurrency={setCount}
                                        currency={count}
                                        digits={3}
                                    />
                                </Field>
                                <Field label="Unit Price" required className={styles.fluid}
                                    validationMessage={unitPrice !== 0 ? undefined : "Unit Price not valid"}>
                                    <CurrencyInput
                                        setCurrency={setUnitPrice}
                                        currency={unitPrice}
                                        digits={3}
                                    />
                                </Field>
                                <Field label={t('VAT')} className={styles.fluid}>
                                    <VATDropdown
                                        currentVAT={vatcode}
                                        setVatCode={(vat: number) => {
                                            setVatcode(vat);
                                        }}
                                    />
                                </Field>
                                <Field label={t('InvoiceRef')}>
                                    <InvoiceDropdown
                                        invoices={invoicesFilter}
                                        currentInvoice={invoiceid ? invoices?.find((i) => i.invoiceid === invoiceid) : undefined}
                                        setInvoice={(i) => { setInvoiceid(i ? i.invoiceid : null); }} />
                                </Field>
                                <div className={styles.onSingleLine}>
                                    <div className={styles.field50}>
                                        <Field label="Peppol Buyer Item Identification" className={styles.fluid}>
                                            <Input
                                                autoComplete="off"
                                                className={styles.fluid}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setBuyerItemIdentification(data.value)}
                                                value={buyerItemIdentification !== null ? buyerItemIdentification : ""}
                                            />
                                        </Field>
                                    </div>
                                    <div className={styles.field50}>
                                        <Field label="Peppol Seller Item Identification" className={styles.fluid}>
                                            <Input
                                                autoComplete="off"
                                                className={styles.fluid}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setSellerItemIdentification(data.value)}
                                                value={sellerItemIdentification !== null ? sellerItemIdentification : ""}
                                            />
                                        </Field>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestSaleEdition(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        disabled={!isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface >
    </Dialog >;
};